import { inject, Injectable } from '@angular/core';
import { TableDataResponse, TablePayload, User } from '../models/users';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { Observable } from 'rxjs';
import { UserDetails } from '../components/user-details/models/user-details';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  readonly defaultConfig: Required<TablePayload> = {
    pageNumber: 1,
    pageSize: 20,
    sort: null,
    search: null
  };

  private http = inject(HttpClient);

  getUsers({ pageSize, pageNumber, search, sort }: Partial<TablePayload>) {
    const body = {
      pageNumber: pageNumber ?? this.defaultConfig.pageNumber,
      pageSize: pageSize ?? this.defaultConfig.pageSize,
      search: search ?? this.defaultConfig.search,
      sort: sort ?? this.defaultConfig.sort
    };
    return this.http.post<TableDataResponse>(`${environment.apiUrl}/users/search`, body);
  }

  getUserById(id: string): Observable<UserDetails> {
    return this.http.get<UserDetails>(`${environment.apiUrl}/users/${id}`);
  }

  checkEmailExists(email: string): Observable<{ exists: boolean }> {
    return this.http.get<{ exists: boolean }>(`${environment.apiUrl}/users/email-exists`, {
      params: { email }
    });
  }

  create(user: UserDetails) {
    return this.http.post<User>(`${environment.apiUrl}/users`, this.mapped(user));
  }

  update(user: UserDetails) {
    return this.http.put<User>(`${environment.apiUrl}/users/${user.id}`, this.mapped(user));
  }

  deleteUserBy(id: number) {
    return this.http.delete(`${environment.apiUrl}/users/${id}`);
  }

  prepareCopyData(user: UserDetails): UserDetails {
    return {
      ...user,
      id: undefined,
      firstName: '',
      lastName: '',
      email: '',
      userType: 'External', // Default to External - will be auto-detected from email
      permissions: this.filterAdminPermissions(user.permissions),
      modules: user.modules,
      shipmentDocumentCategories: user.shipmentDocumentCategories,
      mainPartyNumber: user.mainPartyNumber,
      mainPartyName: user.mainPartyName,
      portalAccess: user.portalAccess,
      // Party visibility settings - copy from source user
      shipmentsAccess: user.shipmentsAccess,
      invoicesAccess: user.invoicesAccess,
      // Party access flags - always false (will be enabled for internal users when @scangl.com email detected)
      canAccessAllCustomers: false,
      canAccessAllInvoices: false
    };
  }

  private filterAdminPermissions(permissions: string[]): string[] {
    return permissions.filter(p => !['Users.Admin', 'Tutorials.Edit'].includes(p));
  }

  private detectUserTypeFromEmail(email: string): 'Internal' | 'External' {
    if (!email || email.indexOf('@') === -1) return 'External';
    const domain = email.substring(email.indexOf('@') + 1);
    return domain.toLowerCase() === 'scangl.com' ? 'Internal' : 'External';
  }

  private mapped({ mainPartyName, shipmentsAccess, invoicesAccess, ...rest }: UserDetails) {
    return {
      ...rest,
      shipmentsAccess: shipmentsAccess.map(shipment => shipment.number),
      invoicesAccess: invoicesAccess.map(invoice => invoice.number)
    };
  }
}
